import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Accordion, AccordionDetails, AccordionSummary, FormControlLabel, IconButton } from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import KogCheckbox from "@/main/components/shared/checkbox/kogCheckbox";
import { getTranslateByKey, isNullUndefinedEmpty } from "@/main/services/utils";
import { getCategory, useCategoryReducer } from "@/main/store/category";
import { getDamageAreas, useDamageAreaReducer } from "@/main/store/damageArea";
import { getPartType, getPartTypesByDirectAssignCriteria, usePartTypeReducer } from "@/main/store/partType";
import OptionCategory, { ICategory } from "./optionCategory/categoryOption";

import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import * as S from "./styles";

interface IApartFilterProps {
	selectedParts: string[];
	selectedDamageArea: string[];
	selectedCategory?: ICategory | null;
	hasScreenParts: boolean;
	handleDrawerClose: () => void;
	handleSelectPart: (part: string) => void;
	handleSelectDamageArea: (damageArea: string) => void;
	handleSelectCategory?: (category: ICategory | null) => void;
	handleClearFilters: () => void;
	useDirectAssignPartTypes?: boolean;
	guidQuote?: string;
}

function AsideFilter({
	selectedParts,
	selectedDamageArea,
	selectedCategory = null,
	hasScreenParts,
	handleDrawerClose,
	handleSelectPart,
	handleSelectDamageArea,
	handleSelectCategory = () => {},
	handleClearFilters,
	useDirectAssignPartTypes = false,
	guidQuote,
}: IApartFilterProps) {
	const { category: categoryReducer } = useCategoryReducer();
	const { partType: partTypeReducer } = usePartTypeReducer();
	const { damageAreas: damageAreaReducer } = useDamageAreaReducer();
	const [selectedParentCategory, setSelectedParentCategory] = useState(null as ICategory | null);
	const dispatch = useDispatch();

	useEffect(() => {
		if (categoryReducer.data === null && !hasScreenParts) dispatch(getCategory());
		if (partTypeReducer.data === null && !useDirectAssignPartTypes) {
			dispatch(getPartType());
		}

		if (damageAreaReducer.data === null) dispatch(getDamageAreas());
	}, []);

	useEffect(() => {
		if (useDirectAssignPartTypes && !isNullUndefinedEmpty(guidQuote)) {
			dispatch(getPartTypesByDirectAssignCriteria({ guidQuote }));
		}
	}, [guidQuote, useDirectAssignPartTypes]);

	return (
		<S.AreaFilter>
			<S.HeaderFilter>
				<h4>{getTranslateByKey("information.filters")}</h4>
				<IconButton onClick={handleDrawerClose} id="id-createOrder-button-closeFilter">
					<ChevronLeftIcon />
				</IconButton>
			</S.HeaderFilter>
			<S.ContentFilter>
				{hasScreenParts && (
					<S.GridCheckbox>
						{partTypeReducer?.data?.map((item) => (
							<FormControlLabel
								key={item.key}
								label={item.value}
								id={`id-createOrder-checkbox-partType-${item.value}`}
								control={
									<KogCheckbox
										name={String(item.key)}
										onChange={() => handleSelectPart(String(item.key))}
										checked={selectedParts.includes(String(item.key))}
										value={selectedParts.includes(String(item.key))}
									/>
								}
							/>
						))}
					</S.GridCheckbox>
				)}

				{!hasScreenParts && (
					<Accordion defaultExpanded>
						<AccordionSummary
							expandIcon={<ExpandMore />}
							aria-controls="categories-content"
							id="id-createOrder-accordion-categories"
						>
							<h5>{getTranslateByKey("information.categories")}</h5>
						</AccordionSummary>
						<AccordionDetails>
							<S.WrapperFilterCategories>
								{categoryReducer?.data?.map((item) => (
									<OptionCategory
										key={item.key}
										category={item}
										selectedCategory={selectedCategory}
										selectedParentCategory={selectedParentCategory}
										handleSelectCategoryParent={(categorySelected) => setSelectedParentCategory(categorySelected)}
										handleSelectCategory={handleSelectCategory}
									/>
								))}
							</S.WrapperFilterCategories>
						</AccordionDetails>
					</Accordion>
				)}

				<S.WrapperFilterDamageArea>
					<h5>{getTranslateByKey("information.damageArea")}</h5>
					<S.GridCheckbox>
						{damageAreaReducer?.data?.map((item) => (
							<FormControlLabel
								key={item.key}
								label={item.value}
								control={
									<KogCheckbox
										name={String(item.key)}
										id={`id-createOrder-checkbox-damageArea-${item.value}`}
										onChange={() => handleSelectDamageArea(String(item.key))}
										checked={selectedDamageArea.includes(String(item.key))}
										value={selectedDamageArea.includes(String(item.key))}
									/>
								}
							/>
						))}
					</S.GridCheckbox>
				</S.WrapperFilterDamageArea>
				<SecondaryButton type="button" onClick={handleClearFilters} id="id-createOrder-button-clearFilters">
					{getTranslateByKey("information.clearFilters")}
				</SecondaryButton>
			</S.ContentFilter>
		</S.AreaFilter>
	);
}

export default AsideFilter;
